// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Form, Button, Icon, Message, Loader } from 'semantic-ui-react';
import { brandingApi, TenantBranding } from '../api/brandingApi';
import BrandingSectionEnhanced from '../components/BrandingSectionEnhanced';

function BrandingEditPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [branding, setBranding] = useState<TenantBranding | null>(null);

  // Track current form values separately from loaded data
  const [brandingTier, setBrandingTier] = useState<'NONE' | 'GREY_LABEL' | 'WHITE_LABEL'>('NONE');
  const [logoUrl, setLogoUrl] = useState('');
  const [brandingConfig, setBrandingConfig] = useState<Record<string, unknown>>({});
  const [defaultBranding, setDefaultBranding] = useState<Record<string, unknown> | undefined>(undefined);

  // Track initial values for change detection
  const initialValues = useRef<{ tier: string; logoUrl: string; branding: string }>({
    tier: '', logoUrl: '', branding: '',
  });

  useEffect(() => {
    if (!tenantId) return;

    const loadBranding = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await brandingApi.getRawBranding(tenantId);
        setBranding(data);
        setBrandingTier(data.brandingTier || 'NONE');
        setLogoUrl(data.logos?.primary || '');
        setBrandingConfig(data.branding || {});

        initialValues.current = {
          tier: data.brandingTier || 'NONE',
          logoUrl: data.logos?.primary || '',
          branding: JSON.stringify(data.branding || {}),
        };
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load branding');
      } finally {
        setLoading(false);
      }
    };

    const loadDefaults = async () => {
      try {
        const config = await brandingApi.getConfig(tenantId);
        setDefaultBranding(config.branding);
      } catch {
        // Non-critical - defaults are optional for reference
      }
    };

    loadBranding();
    loadDefaults();
  }, [tenantId]);

  const handleChange = (field: string, value: unknown) => {
    if (field === 'brandingTier') {
      setBrandingTier(value as 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL');
    } else if (field === 'logoUrl') {
      setLogoUrl(value as string);
    } else if (field === 'branding') {
      setBrandingConfig(value as Record<string, unknown>);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Only call APIs for changed values
      if (brandingTier !== initialValues.current.tier) {
        await brandingApi.updateTier(tenantId, brandingTier);
      }

      const currentLogoUrl = logoUrl;
      if (currentLogoUrl !== initialValues.current.logoUrl) {
        await brandingApi.updateLogos(tenantId, { primary: currentLogoUrl });
      }

      const currentBrandingStr = JSON.stringify(brandingConfig);
      if (currentBrandingStr !== initialValues.current.branding) {
        await brandingApi.updateBranding(tenantId, brandingConfig);
      }

      setSuccess(true);
      setTimeout(() => navigate('/branding'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container style={{ padding: '2rem' }}>
        <Loader active inline='centered'>Loading branding...</Loader>
      </Container>
    );
  }

  return (
    <Container style={{ padding: '2rem' }}>
      <Button
        basic
        onClick={() => navigate('/branding')}
        style={{ marginBottom: '15px' }}
      >
        <Icon name='arrow left' />
        Back to Branding
      </Button>

      <Header as='h1'>
        <Icon name='paint brush' />
        <Header.Content>
          Edit Branding
          <Header.Subheader>
            {branding?.displayName || 'Unknown'}
            {branding?.subdomain && ` (${branding.subdomain})`}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Branding Updated</Message.Header>
          <p>Branding configuration saved successfully. Redirecting...</p>
        </Message>
      )}

      <Form onSubmit={handleSubmit} loading={saving}>
        <BrandingSectionEnhanced
          brandingTier={brandingTier}
          logoUrl={logoUrl}
          branding={brandingConfig}
          onChange={handleChange}
          defaultBranding={defaultBranding}
        />

        <div style={{ marginTop: '20px' }}>
          <Button.Group>
            <Button type='button' onClick={() => navigate('/branding')}>
              Cancel
            </Button>
            <Button.Or />
            <Button positive type='submit' disabled={saving}>
              <Icon name='checkmark' />
              Save Branding
            </Button>
          </Button.Group>
        </div>
      </Form>
    </Container>
  );
}

export default BrandingEditPage;
